import { DateTime as DT } from 'luxon';
import { createPortal } from 'react-dom';
import { useRef, useState } from 'react';
import { useWarehouse } from './context/WarehouseProvider';
import { ProductTitle, useOptionalProduct } from 'modules/product';
import CountBadge from './CounBadge';
import { Dropdown, Spinner } from 'react-bootstrap';
import { format$Price, formatPrice } from 'utils/helpers/functions/formatPrice';
import { withLocalCurrency } from 'utils/helpers/currency/currencyHelpers';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { canWarehouseOps, hidesPurchasePrices, isSellerUser, splitsStockByPurchasePrice } from 'utils/helpers/access/hasUserAccess';
import { useTranslation } from 'react-i18next';
import vconst from 'utils/helpers/structs/vconst';
import CopyTextWrapper from 'components/others/CopyTextWrapper';
import SolarIcon from 'components/icons/SolarIcon';
import { writeOffDeletedStock } from './model/writeOffDeletedStock';
import StockLotsTable from './OffStockLots/StockLotsTable';
import type { StockLotRow } from './OffStockLots/StockLotsTable/types';

const formatStockAmount = (amount: number, isUahMode: boolean) => {
    if (isUahMode) {
        return amount >= 0
            ? withLocalCurrency(formatPrice(amount))
            : withLocalCurrency(formatPrice(-amount), true);
    }
    return format$Price(amount);
};

const StockTableRows = ({
    wId,
    stock,
    setShowTransfer,
    setShowComing,
    setShowPaymentsId,
    setShowPaymentsTitle,
    setShowStockLots,
    stockLots,
    fetchStocks,
    onlyView,
}: any) => {
    const { setWOperation, showCredit, showCategory, showArticle, showUpdated, showLotBreakdown } = useWarehouse();
    const openProductCard = useOptionalProduct()?.openProductCard;
    const { auth } = useAuth();
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const writeOffLockRef = useRef(false);
    const [writeOffLoading, setWriteOffLoading] = useState(false);
    const isUahMode = (Number(localSettings?.RATE?.current ?? 1) || 1) === 1;
    const { t } = useTranslation(['Warehouses']);
    const canOps = canWarehouseOps(auth?.user);
    const canTransfer = canOps || isSellerUser(auth?.user);
    const canCredit = Boolean(params.creditControl);
    const dtU = DT.fromMillis(stock?.stockUpdated || 0).toFormat('dd.MM.yyyy');
    const isMainWarehouse = Number(wId) === vconst.waherouseMainId;
    const isDeleted = Number(stock?.deleted) === 1;
    const canWriteOff =
        isDeleted &&
        Number(stock?.stockCount) > 0 &&
        Number(wId) !== vconst.waherouseWriteOffId;
    const showCreditAmount =
        Boolean(stock.stockBalance) &&
        showCredit &&
        auth?.user?.access?.role == 'manager' &&
        isMainWarehouse;

    const productLots: StockLotRow[] | undefined = stockLots?.[Number(stock?.productId || 0)];
    const showLotsTable = Boolean(showLotBreakdown && productLots && productLots.length > 0);
    const showLotsZero = Boolean(showLotBreakdown && productLots && productLots.length === 0);

    const creditAmountNode = showCreditAmount ? (
        <span className={`fs-12 ${stock.stockBalance < 0 ? 'text-warning' : 'text-danger'}`}>
            {formatStockAmount(-stock.stockBalance, isUahMode)}
        </span>
    ) : null;

    const handleWriteOff = async () => {
        if (!canWriteOff || writeOffLockRef.current) return;
        writeOffLockRef.current = true;
        setWriteOffLoading(true);
        try {
            const splitByPrice = splitsStockByPurchasePrice(auth?.user, Boolean(params.ignoreWarehousePrice));
            const resTransfer = await writeOffDeletedStock(Number(wId), stock, splitByPrice);
            if (resTransfer.stat) {
                showToast(t('messTransferOk'), 'success');
                await Promise.resolve(fetchStocks?.(true));
                return;
            }
            switch (resTransfer.errCode) {
                case 'NO_PRODUCT_STOCK':
                    showToast(t('messNoProductsStock'), 'danger');
                    break;
                case 'WAREHOUSE_CLOSED':
                    showToast(t('messWarehouseClosed'), 'danger');
                    break;
                case 'TEAM_DELETED':
                    showToast(t('messTeamDeleted'), 'danger');
                    break;
                case 'CUSTOM_DELETED':
                    showToast(t('messCustomDeleted'), 'danger');
                    break;
                default:
                    showToast(t('common:messError'), 'danger');
            }
        } finally {
            writeOffLockRef.current = false;
            setWriteOffLoading(false);
        }
    };

    const renderMenuItem = (
        icon: React.ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { variant?: 'danger' },
    ) => (
        <Dropdown.Item
            as="button"
            className={options?.variant === 'danger' ? 'text-danger' : undefined}
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span
                    className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${
                        options?.variant === 'danger' ? ' text-danger' : ''
                    }`}
                >
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className={`fw-medium ${options?.variant === 'danger' ? 'text-danger' : ''}`}>{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    return (
        <tr className={isDeleted ? 'bg-light-danger' : undefined}>
            <td className="name">
                <ProductTitle
                    product={stock}
                    onClick={() => {
                        setWOperation({
                            warehouseId: stock?.warehouseId,
                            productId: stock?.productId,
                            stockCount: stock?.stockCount,
                        });
                    }}
                    onPhotoClick={
                        openProductCard
                            ? () => {
                                  const productId = Number(stock?.productId || 0);
                                  if (productId > 0) openProductCard(productId, 'photo');
                              }
                            : undefined
                    }
                />
            </td>
            {showCategory ? (
            <td className="category">
                <h6 className="mb-1 text-break text-truncate" title={stock.categoryName || ''}>
                    {stock.categoryName || '-'}
                </h6>
            </td>
            ) : null}
            {showArticle ? (
            <td className="article text-truncate">
                <CopyTextWrapper value={stock.prodArticle || ''}>
                    <span title={stock.prodArticle}>{stock.prodArticle}</span>
                </CopyTextWrapper>
            </td>
            ) : null}
            {showCredit &&
                auth?.user?.access?.role == 'manager' &&
                !hidesPurchasePrices(auth?.user) &&
                isMainWarehouse && (
                    <td className="balance text-end">
                        {creditAmountNode && canCredit ? (
                            <span
                                className="cursor-pointer"
                                role="button"
                                title={t('toolPayments')}
                                onClick={() => {
                                    setShowPaymentsId(stock?.productId);
                                    setShowPaymentsTitle(stock?.prodName);
                                }}
                            >
                                {creditAmountNode}
                            </span>
                        ) : (
                            creditAmountNode
                        )}
                    </td>
                )}
            {showUpdated ? (
            <td className="updated">
                <span className="cursor-pointer text-break">{dtU}</span>
            </td>
            ) : null}
            <td className="count">
                {showLotBreakdown ? (
                    showLotsTable ? (
                        <StockLotsTable rows={productLots || []} plainStock />
                    ) : showLotsZero ? (
                        <span className="fs-12">0</span>
                    ) : null
                ) : (
                    <CountBadge fs={12} count={stock.stockCount} />
                )}
                {creditAmountNode && <span className="d-block pt-2 d-lg-none">{creditAmountNode}</span>}
            </td>
            {!onlyView && canTransfer && (
                <td className="action">
                    <div className="d-flex align-items-center justify-content-end gap-1">
                        <Dropdown align="end">
                            <Dropdown.Toggle
                                className="p-1 py-0 warehouse-team-menu-toggle"
                                size="sm"
                                variant="light-secondary"
                                id={`dropdown-stock-${stock.ID}`}
                                disabled={writeOffLoading}
                            >
                                {writeOffLoading
                                    ? <Spinner animation="border" size="sm" />
                                    : <SolarIcon name="three-dots" />}
                            </Dropdown.Toggle>
                            {typeof document !== 'undefined'
                                ? createPortal(
                                      <Dropdown.Menu
                                          className="p-2 shadow-sm warehouse-team-menu"
                                          popperConfig={{ strategy: 'fixed' }}
                                          renderOnMount
                                      >
                                          {renderMenuItem(
                                              <SolarIcon name="transfer" />,
                                              t('toolTransfer'),
                                              t('toolTransferHint'),
                                              () => setShowTransfer(stock?.productId),
                                          )}
                                          {canOps ? renderMenuItem(
                                              <SolarIcon name="list-ul" />,
                                              t('toolStockLots'),
                                              t('toolStockLotsHint'),
                                              () => setShowStockLots?.(stock),
                                          ) : null}
                                          {canOps && isMainWarehouse ? (
                                              renderMenuItem(
                                                  <SolarIcon name="box-arrow-in-down" />,
                                                  t('toolComming'),
                                                  t('toolCommingHint'),
                                                  () => setShowComing(stock?.productId),
                                              )
                                          ) : null}
                                          {canOps && isMainWarehouse && canCredit ? (
                                              <>
                                                  <Dropdown.Divider className="my-2" />
                                                  {renderMenuItem(
                                                      <SolarIcon name="cash-coin" />,
                                                      t('toolPayments'),
                                                      t('toolPaymentsHint'),
                                                      () => {
                                                          setShowPaymentsId(stock?.productId);
                                                          setShowPaymentsTitle(stock?.prodName);
                                                      },
                                                  )}
                                              </>
                                          ) : null}
                                          {canOps && canWriteOff ? (
                                              <>
                                                  <Dropdown.Divider className="my-2" />
                                                  {renderMenuItem(
                                                      <SolarIcon name="trash" />,
                                                      t('toolWriteOff'),
                                                      t('toolWriteOffHint'),
                                                      () => {
                                                          void handleWriteOff();
                                                      },
                                                      { variant: 'danger' },
                                                  )}
                                              </>
                                          ) : null}
                                      </Dropdown.Menu>,
                                      document.body,
                                  )
                                : null}
                        </Dropdown>
                    </div>
                </td>
            )}
        </tr>
    );
};

export default StockTableRows;
